import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Layers, X, CheckCircle2, AlertCircle, Loader2, UploadCloud } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { grabFrame } from "@/lib/frameGrab";
import { uploadVideoInChunks, STAGE_LABEL } from "@/lib/chunkUpload";

const naturalSort = (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
const titleOf = (name) => name.replace(/\.[^.]+$/, "").replace(/[._-]+/g, " ").trim();
const fmtMB = (b) => `${(b / 1048576).toFixed(1)} MB`;

export const BulkUploader = ({ list, selected, setSelected, onUploaded }) => {
  const [season, setSeason] = useState(1);
  const [startAt, setStartAt] = useState(1);
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState({});
  const [running, setRunning] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!selected) return;
    api.get(`/anime/${selected}`).then((r) => {
      const nums = r.data.episodes.filter((e) => (e.season || 1) === Number(season)).map((e) => e.number);
      setStartAt(nums.length ? Math.max(...nums) + 1 : 1);
    });
  }, [selected, season]);

  const addFiles = (incoming) => {
    const vids = Array.from(incoming).filter((f) => f.type.startsWith("video/"));
    if (vids.length !== incoming.length) toast.error("Only video files (MP4/MKV/WebM/MOV) are accepted");
    setFiles((prev) => [...prev, ...vids.filter((f) => !prev.some((p) => p.name === f.name && p.size === f.size))].sort(naturalSort));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    addFiles(e.dataTransfer.files);
  };

  const removeFile = (i) => setFiles((p) => p.filter((_, k) => k !== i));

  const run = async () => {
    if (!selected) return toast.error("Select an anime first");
    if (!files.length) return toast.error("Choose at least one MP4 file");
    setRunning(true);
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const fd = new FormData();
      fd.append("season", season);
      fd.append("number", Number(startAt) + i);
      fd.append("title", titleOf(f.name));
      const thumb = await grabFrame(f);
      if (thumb) fd.append("thumbnail", thumb);
      setStatus((s) => ({ ...s, [i]: { state: "uploading", pct: 0 } }));
      try {
        const up = await uploadVideoInChunks(f, (pct, stage) => setStatus((s) => ({ ...s, [i]: { state: "uploading", pct, stage } })));
        fd.append("video_url", up.url);
        fd.append("original_filename", up.filename);
        fd.append("size", up.size);
        await api.post(`/anime/${selected}/episodes`, fd);
        setStatus((s) => ({ ...s, [i]: { state: "done", pct: 100, warning: up.warning } }));
        if (up.warning) toast.warning(`${f.name}: ${up.warning}`, { duration: 15000 });
        ok++;
      } catch (err) {
        setStatus((s) => ({ ...s, [i]: { state: "error", msg: errMsg(err) } }));
      }
    }
    setRunning(false);
    toast[ok === files.length ? "success" : "warning"](`${ok}/${files.length} episodes uploaded`);
    onUploaded?.();
    if (ok === files.length) {
      setFiles([]);
      setStatus({});
    }
  };

  return (
    <div data-testid="bulk-uploader" className="kp-glass p-7 kp-card-clip flex flex-col gap-4">
      <div>
        <span className="kp-eyebrow">Bulk</span>
        <h2 className="font-display font-bold text-xl mt-1">Multiple Episodes Upload</h2>
        <p className="text-[#94A3B8] text-xs mt-2 leading-relaxed">Drop many MP4 files at once — they are auto-numbered in filename order and titled from the filename.</p>
      </div>
      <select data-testid="bulk-anime-select" className="kp-input" value={selected} onChange={(e) => setSelected(e.target.value)}>
        <option value="">Select anime *</option>
        {list.map((a) => <option key={a.id} value={a.id}>{`${a.title} (${a.episode_count} ep)`}</option>)}
      </select>
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1 text-xs text-[#94A3B8]">Season
          <input data-testid="bulk-season-input" type="number" min="1" className="kp-input" value={season} onChange={(e) => setSeason(Number(e.target.value) || 1)} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-[#94A3B8]">Start from Ep #
          <input data-testid="bulk-start-input" type="number" min="1" className="kp-input" value={startAt} onChange={(e) => setStartAt(Number(e.target.value) || 1)} />
        </label>
      </div>

      <div
        data-testid="bulk-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`flex flex-col items-center justify-center gap-2 border border-dashed px-4 py-8 cursor-pointer text-sm transition-colors ${drag ? "border-[#00F0FF] bg-[#00F0FF]/5" : "border-[#FF2A55]/40 hover:border-[#FF2A55] bg-[#FF2A55]/5"}`}
      >
        <Layers size={22} className="text-[#FF2A55]" />
        <span className="text-[#94A3B8]">Drop MP4 files here or click to browse</span>
        <input ref={inputRef} data-testid="bulk-file-input" type="file" multiple accept="video/*,.mp4,.mkv,.webm,.mov,.m4v" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
      </div>

      {files.length > 0 && (
        <div data-testid="bulk-file-list" className="flex flex-col divide-y divide-white/[0.06] border-y border-white/[0.06] max-h-72 overflow-y-auto">
          {files.map((f, i) => {
            const st = status[i];
            return (
              <div key={`${f.name}-${f.size}`} data-testid={`bulk-file-row-${i}`} className="flex items-center gap-3 py-2.5 text-sm">
                <span className="font-mono text-xs text-[#00F0FF] w-14 shrink-0">S{season} E{Number(startAt) + i}</span>
                <div className="flex-1 min-w-0">
                  <div className="truncate">{titleOf(f.name)}</div>
                  <div className="text-[0.65rem] font-mono text-[#64748B] truncate">{f.name} · {fmtMB(f.size)}{st?.state === "error" ? ` · ${st.msg}` : ""}{st?.state === "uploading" && STAGE_LABEL[st.stage] ? ` · ${STAGE_LABEL[st.stage]}` : ""}</div>
                  {st?.state === "uploading" && <div className="h-1 bg-white/10 mt-1.5"><div className="h-full bg-gradient-to-r from-[#FF2A55] to-[#00F0FF] transition-[width]" style={{ width: `${st.pct}%` }} /></div>}
                </div>
                {st?.state === "done" && <CheckCircle2 size={16} className="text-[#00F0FF]" />}
                {st?.state === "error" && <AlertCircle size={16} className="text-[#FF2A55]" />}
                {st?.state === "uploading" && <Loader2 size={16} className="animate-spin text-[#94A3B8]" />}
                {!st && !running && <button data-testid={`bulk-file-remove-${i}`} onClick={() => removeFile(i)} className="p-1 text-[#64748B] hover:text-[#FF2A55] transition-colors" aria-label="Remove"><X size={14} /></button>}
              </div>
            );
          })}
        </div>
      )}

      <button data-testid="bulk-upload-submit" onClick={run} disabled={running || !files.length} className="kp-btn kp-btn-primary justify-center disabled:opacity-60">
        <UploadCloud size={14} /> {running ? "Uploading..." : `Upload ${files.length || ""} Episode${files.length === 1 ? "" : "s"}`}
      </button>
    </div>
  );
};
